import { Injectable, inject, effect } from '@angular/core';
import { StoresApiService } from '../../../core/stores/stores-api.service';
import { CashDrawersApiService } from '../../cash-drawers/cash-drawers-api.service';
import { PosOfflineDbService } from './pos-offline-db.service';
import { OfflineService } from './offline.service';
import { PosOfflineWrapperService } from './pos-offline-wrapper.service';
import { StockLayerGatewayService } from '../../stock-layer-gateway/stock-layer-gateway.service';
import { CurrentDeviceService } from '../current-device/current-device.service';

@Injectable({
  providedIn: 'root',
})
export class PosDataPreloaderService {
  #storesService = inject(StoresApiService);
  #cashDrawersService = inject(CashDrawersApiService);
  #offlineDbService = inject(PosOfflineDbService);
  #offlineService = inject(OfflineService);
  #offlineWrapperService = inject(PosOfflineWrapperService);
  #stockGatewayService = inject(StockLayerGatewayService);
  #currentDevice = inject(CurrentDeviceService);

  constructor() {
    effect(() => {
      const storeId = this.#currentDevice.storeId();
      const isOnline = this.#offlineService.isOnline();

      if (storeId) {
        if (isOnline) {
          this.preloadPosData(storeId);
        } else {
          this.checkOfflineDataAvailability(storeId);
        }
      }
    });

    effect(() => {
      const registerId = this.#currentDevice.registerId();
      const isOnline = this.#offlineService.isOnline();

      if (registerId && isOnline) {
        this.preloadRegisterSession(registerId);
      }
    });
  }

  /**
   * Preload register session data for offline use
   */
  private async preloadRegisterSession(registerId: number): Promise<void> {
    try {
      await this.#offlineWrapperService.findLatestCashRegisterSession(registerId);
    } catch (error) {
      console.error('Failed to preload register session:', error);
    }
  }

  private async checkOfflineDataAvailability(storeId: number): Promise<void> {
    try {
      const products = await this.#offlineDbService.getProducts(storeId);
      const categories = await this.#offlineDbService.getCategories(storeId);
      const customers = await this.#offlineDbService.getAllCustomers(storeId);

      if (products.length === 0 || categories.length === 0) {
        console.warn(`No offline data cached for store ${storeId}`);
      }
    } catch (error) {
      console.error('[POS Preloader] Failed to check offline data:', error);
    }
  }

  private async preloadPosData(storeId: number): Promise<void> {
    try {
      const existingProducts = await this.#offlineDbService.getProducts(storeId);
      const existingCategories = await this.#offlineDbService.getCategories(storeId);

      if (existingProducts.length === 0 || existingCategories.length === 0) {
        await this.fetchAndCacheData(storeId);
        return;
      }

      const lastUpdate = await this.#offlineDbService.getMetadata(`last_update_${storeId}`);
      const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;

      if (lastUpdate && lastUpdate > thirtyMinutesAgo) {
        return;
      }

      await this.fetchAndCacheData(storeId);
    } catch (error) {
      console.error('[POS Preloader] Failed to preload data:', error);
    }
  }

  private async fetchAndCacheData(storeId: number): Promise<void> {
    const { products, categories } = await this.#storesService.findStorePos(storeId, { page: 1, size: 1000000 });

    await this.#offlineDbService.saveProducts(storeId, products.data);
    await this.#offlineDbService.saveCategories(storeId, categories);
    await this.#offlineDbService.saveMetadata(`last_update_${storeId}`, Date.now());
    await this.preloadAdditionalData(storeId);
  }

  private async preloadAdditionalData(storeId: number): Promise<void> {
    try {
      await Promise.all([
        this.#offlineWrapperService.preloadCustomers(storeId),
        this.#offlineWrapperService.preloadDiscounts(storeId),
      ]);
    } catch (error) {
      console.error('[POS Preloader] Failed to preload additional data:', error);
    }
  }

  /**
   * Force reload POS data for a store
   */
  async forceReloadPosData(storeId: number): Promise<void> {
    try {
      const { products, categories } = await this.#storesService.findStorePos(storeId, { page: 1, size: 1000000 });

      await this.#offlineDbService.saveProducts(storeId, products.data);
      await this.#offlineDbService.saveCategories(storeId, categories);
      await this.#offlineDbService.saveMetadata(`last_update_${storeId}`, Date.now());
      await this.preloadAdditionalData(storeId);
    } catch (error) {
      console.error('[POS Preloader] Failed to reload data:', error);
      throw error;
    }
  }

  /**
   * Preload ALL critical data for offline use.
   * This should be called after login to ensure offline functionality works immediately.
   * Returns a promise that resolves when all data is preloaded.
   */
  async preloadAllCriticalData(): Promise<void> {
    if (this.#offlineService.isOffline()) {
      return;
    }

    try {
      const stores = await this.#storesService.findStoreNames();
      await this.#offlineDbService.saveStoreList(stores);

      if (stores.length === 0) {
        return;
      }

      const storeId = this.#currentDevice.storeId() || stores[0].id;

      const registers = await this.#cashDrawersService.findRegisterNames();
      await this.#offlineDbService.saveRegisterList(registers);

      const { products, categories } = await this.#storesService.findStorePos(storeId, { page: 1, size: 1000000 });
      await this.#offlineDbService.saveProducts(storeId, products.data);
      await this.#offlineDbService.saveCategories(storeId, categories);
      await this.#offlineDbService.saveMetadata(`last_update_${storeId}`, Date.now());

      await this.#offlineWrapperService.preloadCustomers(storeId);
      await this.#offlineWrapperService.preloadDiscounts(storeId);

      this.#stockGatewayService.subscribeToProductStocks(storeId);

      const registerId = this.#currentDevice.registerId();
      if (registerId) {
        await this.#offlineWrapperService.findLatestCashRegisterSession(registerId);
      } else if (registers.length > 0) {
        const storeRegisters = registers.filter((r: any) => r.storeId === storeId);
        if (storeRegisters.length > 0) {
          await this.#offlineWrapperService.findLatestCashRegisterSession(storeRegisters[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to preload critical data:', error);
      throw error;
    }
  }
}
